import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, Typography,
  CircularProgress,
} from "@mui/material";
import { createCollector, PAYMENT_PROCESSORS } from "../../services/collectors";
import { checkCollectorId } from "../../lib/collectorId";

interface Props {
  open:        boolean;
  existingIds: string[];
  onClose:     () => void;
  onCreated:   (id: string) => void;
}

/** Alta de collector: solo identidad (id, procesador, DBA). El resto va por Config.
 * CP-409: el Id se guarda con la caja tal como se tipea (regla en lib/collectorId). */
export default function NewCollectorDialog({ open, existingIds, onClose, onCreated }: Props) {
  const [id, setId]     = useState("");
  const [proc, setProc] = useState<string>(PAYMENT_PROCESSORS[0]);
  const [dba, setDba]   = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  useEffect(() => { if (open) { setId(""); setProc(PAYMENT_PROCESSORS[0]); setDba(""); setErr(null); } }, [open]);

  const { id: idClean, ok: idOk, help } = useMemo(() => checkCollectorId(id, existingIds), [id, existingIds]);

  async function onSave() {
    setBusy(true); setErr(null);
    try {
      await createCollector({ id: idClean, paymentsProcessor: proc, doingBusinessAs: dba.trim() });
      onCreated(idClean);
    } catch (e: any) { setErr(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        New collector
        <Typography variant="body2" color="text.secondary">Only the identity is set here; everything else is configured afterwards.</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Id" value={id} autoFocus disabled={busy} placeholder="e.g. AguasRenzi"
            onChange={(e) => setId(e.target.value)}
            error={!!idClean && !idOk} helperText={help}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }} />
          <TextField select label="Payment processor" value={proc} disabled={busy} onChange={(e) => setProc(e.target.value)}
            helperText="Country · processor. Determines which bank connector issues the accounts."
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}>
            {PAYMENT_PROCESSORS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField label="Doing business as" value={dba} disabled={busy} placeholder="Name shown to payers"
            onChange={(e) => setDba(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }} />
          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={busy || !idOk}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}>
          {busy ? "Creating…" : "Create collector"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
